import React, { FC, useState, useRef, useMemo } from 'react'

import { PopupActions } from 'reactjs-popup/dist/types'
import CustomPopup from '../../UI/Popup/index'
import Select from 'react-select'
import NumberInput from '../../UI/Inputs/NumberInput'
import CustomButton from '../../UI/Buttons/CustomButton'

import {
  CANVA_DESIGN_TYPE,
  TCanvaDesignTypeName,
  MEASURE_TYPE,
  TCanvaDesignType,
  DESIGN_TYPE,
  TCustomCanvaDesignType,
  DESIGN_TYPE_FROM_RESTRICTION_FOR_CANVA
} from '../../../services/canva'

import CanvaButton, {
  CanvaButtonActions,
  TCanvaPayload
} from '../../PostCreation/CanvaButton/CanvaButton'
import './CanvaSettingsDialog.scss'

const CUSTOM_NAME_STRING: string = 'CustomType'

type TMeasureOption = {
  value: MEASURE_TYPE
  label: MEASURE_TYPE
}

const customDesignType: TCustomCanvaDesignType = {
  type: DESIGN_TYPE.CUSTOM_TYPE,
  name: CUSTOM_NAME_STRING,
  width: 1000,
  height: 1000,
  measure: MEASURE_TYPE.TYPE_PX
}

const allCanvaTypesForChosenList = Object.assign(
  {},
  CANVA_DESIGN_TYPE,
  DESIGN_TYPE_FROM_RESTRICTION_FOR_CANVA
)

const canvaDesignTypeNames: TCanvaDesignTypeName[] = Object.keys(
  CANVA_DESIGN_TYPE
) as TCanvaDesignTypeName[]

const customDesignTypeNames: string[] = Object.keys(DESIGN_TYPE_FROM_RESTRICTION_FOR_CANVA)

const measureValues: TMeasureOption[] = Object.values(MEASURE_TYPE).map((key) => ({
  label: key,
  value: key
}))

const RANGE_VALUES_BY_UNITS: { [key in MEASURE_TYPE]: { min: number; max: number } } = {
  cm: {
    min: 1.06,
    max: 211
  },
  px: {
    min: 40,
    max: 8000
  },
  mm: {
    min: 10.59,
    max: 2116
  },
  in: {
    min: 0.418,
    max: 83.3
  }
}

interface CanvaSettingsDialogProps {
  updateImageData: (payload: TCanvaPayload) => void
}

const CanvaSettingsDialog: FC<CanvaSettingsDialogProps> = ({ updateImageData }) => {
  const popupRef = useRef<PopupActions>()
  const canvaButtonRef = useRef<CanvaButtonActions>(null)

  const [isOpen, setIsOpen] = useState<boolean>(false)

  const [searchQuery, setSearchQuery] = useState<string>('')
  const [selectedDesignType, setSelectedDesignType] = useState<
    TCustomCanvaDesignType | TCanvaDesignType
  >(customDesignType)

  const canvaDesignTypeNamesFilteredByQuery = useMemo(() => {
    if (searchQuery.length) {
      return canvaDesignTypeNames.filter((designName) =>
        designName
          .replace(/([a-zA-Z])(?=[A-Z])/g, '$1 ')
          .toLocaleLowerCase()
          .includes(searchQuery.toLocaleLowerCase())
      )
    } else {
      return canvaDesignTypeNames
    }
  }, [searchQuery])

  const customDesignTypeNamesFilteredByQuery = useMemo(() => {
    if (searchQuery.length) {
      return customDesignTypeNames.filter((designName) =>
        designName
          .replace(/([a-zA-Z])(?=[A-Z])/g, '$1 ')
          .toLocaleLowerCase()
          .includes(searchQuery.toLocaleLowerCase())
      )
    } else {
      return customDesignTypeNames
    }
  }, [searchQuery])

  const selecteCanvaType = (key: TCanvaDesignTypeName | string) => {
    setSelectedDesignType(allCanvaTypesForChosenList[key])
  }

  const closeModal = () => {
    setSearchQuery('')
    setSelectedDesignType(customDesignType)
    setIsOpen(false)
  }

  const changeMeasure = (value: any, action: any) => {
    const unit: MEASURE_TYPE = value.value
    let { width, height } = selectedDesignType

    if (!(width >= RANGE_VALUES_BY_UNITS[unit].min && width <= RANGE_VALUES_BY_UNITS[unit].max)) {
      if (width < RANGE_VALUES_BY_UNITS[unit].min) width = RANGE_VALUES_BY_UNITS[unit].min
      else width = RANGE_VALUES_BY_UNITS[unit].max
    }
    if (!(height >= RANGE_VALUES_BY_UNITS[unit].min && height <= RANGE_VALUES_BY_UNITS[unit].max)) {
      if (height < RANGE_VALUES_BY_UNITS[unit].min) height = RANGE_VALUES_BY_UNITS[unit].min
      else height = RANGE_VALUES_BY_UNITS[unit].max
    }

    setSelectedDesignType((prevState: TCanvaDesignType | TCustomCanvaDesignType) => ({
      ...prevState,
      measure: unit,
      height,
      width
    }))
  }

  const handleOnBlurWidthHeight = ({ value, name }: any) => {
    let result = value
    const unit: MEASURE_TYPE = selectedDesignType.measure

    if (!(result >= RANGE_VALUES_BY_UNITS[unit].min && result <= RANGE_VALUES_BY_UNITS[unit].max)) {
      if (result < RANGE_VALUES_BY_UNITS[unit].min) result = RANGE_VALUES_BY_UNITS[unit].min
      else result = RANGE_VALUES_BY_UNITS[unit].max
    }

    setSelectedDesignType((prevState: TCanvaDesignType | TCustomCanvaDesignType) => ({
      ...prevState,
      [name]: +result
    }))
  }

  const changeWidthHeight = ({ name, value }: { value: string; name: string }) => {
    setSelectedDesignType((prevState) => ({ ...prevState, [name]: Number.parseFloat(value) }))
  }

  const openCanvaWindow = () => {
    canvaButtonRef.current?.createDesign(selectedDesignType)
    closeModal()
  }

  return (
    <>
      <CustomPopup
        open={isOpen}
        customRefProp={popupRef}
        className='canva-settings-dialog'
        position='top center'
        onClose={closeModal}
        arrow={false}
      >
        <div className='canva-settings-dialog-popup'>
          <div className='canva-settings-dialog-popup_input'>
            <div className='canva-settings-dialog-popup-search'></div>
            <div className='canva-settings-dialog-popup_type-name'>
              <input
                placeholder='I’m looking for design type...'
                className='canva-settings-dialog-popup_type-name-input'
                value={searchQuery}
                onChange={({ currentTarget: { value } }: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchQuery(value)
                }
              />
            </div>
            <div className='canva-settings-dialog-popup_clear'>
              <button
                className='canva-settings-dialog-popup_clear-button'
                onClick={() => setSearchQuery('')}
              >
                Clear
              </button>
            </div>
          </div>
          <div className='canva-settings-dialog-popup_result'>
            <div className='canva-settings-dialog-popup_result-title'>
              {selectedDesignType.name.replace(/([a-zA-Z])(?=[A-Z])/g, '$1 ')}
            </div>
            <div className={`canva-settings-dialog-popup_inputs-container`}>
              <NumberInput
                handleChange={changeWidthHeight}
                onBlur={handleOnBlurWidthHeight}
                name='width'
                value={selectedDesignType.width}
                {...RANGE_VALUES_BY_UNITS[selectedDesignType.measure]}
              />
              <NumberInput
                handleChange={changeWidthHeight}
                onBlur={handleOnBlurWidthHeight}
                name='height'
                value={`${selectedDesignType.height}`}
                {...RANGE_VALUES_BY_UNITS[selectedDesignType.measure]}
              />
              <Select
                isSearchable={false}
                options={measureValues}
                className='measure-select'
                classNamePrefix='measure-select'
                onChange={changeMeasure}
                value={{ value: selectedDesignType.measure, label: selectedDesignType.measure }}
              />
            </div>
            <div className='canva-settings-dialog-popup_result-list'>
              {!!canvaDesignTypeNamesFilteredByQuery.length && (
                <>
                  <div className='canva-settings-dialog-popup_result-title'>Canva Design Types</div>
                  {canvaDesignTypeNamesFilteredByQuery.map((key: TCanvaDesignTypeName, index) => (
                    <div
                      key={CANVA_DESIGN_TYPE[key].name}
                      className={`canva-settings-dialog-popup_result-list-item
                          ${
                            CANVA_DESIGN_TYPE[key].name === selectedDesignType.name &&
                            'canva-settings-dialog-popup_result-list-item_selected'
                          }`}
                      onClick={() => selecteCanvaType(key)}
                    >
                      <div className='canva-settings-dialog-popup_result-list-item-name'>
                        {CANVA_DESIGN_TYPE[key].name.replace(/([a-zA-Z])(?=[A-Z])/g, '$1 ')}
                      </div>
                      <div className='canva-settings-dialog-popup_result-list-item-sizes'>
                        {CANVA_DESIGN_TYPE[key].width} x {CANVA_DESIGN_TYPE[key].height}{' '}
                        {CANVA_DESIGN_TYPE[key].measure}
                      </div>
                    </div>
                  ))}
                </>
              )}
              {!!customDesignTypeNamesFilteredByQuery.length && (
                <>
                  <div className='canva-settings-dialog-popup_result-title'>
                    Attention Nest Custom Types
                  </div>
                  {customDesignTypeNamesFilteredByQuery.map((key: string) => (
                    <div
                      key={DESIGN_TYPE_FROM_RESTRICTION_FOR_CANVA[key].name}
                      className={`canva-settings-dialog-popup_result-list-item
                          ${
                            DESIGN_TYPE_FROM_RESTRICTION_FOR_CANVA[key].name ===
                              selectedDesignType.name &&
                            'canva-settings-dialog-popup_result-list-item_selected'
                          }`}
                      onClick={() => selecteCanvaType(key)}
                    >
                      <div className='canva-settings-dialog-popup_result-list-item-name'>
                        {DESIGN_TYPE_FROM_RESTRICTION_FOR_CANVA[key].name.replace(
                          /([a-zA-Z])(?=[A-Z])/g,
                          '$1 '
                        )}
                      </div>
                      <div className='canva-settings-dialog-popup_result-list-item-sizes'>
                        {DESIGN_TYPE_FROM_RESTRICTION_FOR_CANVA[key].width} x{' '}
                        {DESIGN_TYPE_FROM_RESTRICTION_FOR_CANVA[key].height}{' '}
                        {DESIGN_TYPE_FROM_RESTRICTION_FOR_CANVA[key].measure}
                      </div>
                    </div>
                  ))}
                </>
              )}
              {!canvaDesignTypeNamesFilteredByQuery.length &&
                !customDesignTypeNamesFilteredByQuery.length &&
                !!searchQuery.length && (
                  <div className='canva-settings-dialog-popup_result_empty'>No options</div>
                )}
            </div>
          </div>
          <div className='canva-settings-dialog-popup_actions'>
            <CustomButton size='xs' onClick={closeModal} button='secondary'>
              Close
            </CustomButton>
            <CustomButton size='xs' button='primary' onClick={openCanvaWindow}>
              Continue
            </CustomButton>
          </div>
        </div>
      </CustomPopup>
      <span
        className='canva-btn canva-btn-theme-default canva-btn-size-s'
        onClick={() => setIsOpen(true)}
      >
        <span className='canva-btn-i'></span>
      </span>

      <div style={{ display: 'none' }}>
        <CanvaButton ref={canvaButtonRef} updateImageData={updateImageData} />
      </div>
    </>
  )
}

export default CanvaSettingsDialog
